import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface BillFilters {
  search?: string;
  customer_id?: string;
  supplier_id?: string;
  account_id?: string;
  status?: string;
  parsed?: boolean;
  invoice_date_from?: string;
  invoice_date_to?: string;
  limit?: number;
  offset?: number;
}

export function useCollectionStats() {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['collection-stats', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('bills-manage', {
        body: { action: 'collection_stats' },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useCollectionList(filters?: BillFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['collection-list', currentWorkspace?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('bills-manage', {
        body: { action: 'collection_list', filters },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useBulkDownload() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (billIds: string[]) => {
      const { data, error } = await supabase.functions.invoke('bills-manage', {
        body: { action: 'bulk_download', bill_ids: billIds },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.zip_url) {
        // Trigger download
        const link = document.createElement('a');
        link.href = data.zip_url;
        link.download = 'bills.zip';
        link.click();
        
        toast({
          title: 'Download started',
          description: `Downloading ${data.count} bill(s)`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Bulk download failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useManualUpload() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (uploadData: {
      customer_id: string;
      supplier_id: string;
      parser_type: 'automation' | 'manual';
      files: Array<{
        file_url: string;
        file_name: string;
        file_size: number;
        file_type: string;
      }>;
    }) => {
      const { data, error } = await supabase.functions.invoke('bills-manage', {
        body: { action: 'manual_upload', ...uploadData },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      toast({
        title: 'Upload successful',
        description: 'Bills uploaded and queued for processing',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useBillDetails(billId: string) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['bill-details', currentWorkspace?.id, billId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('bills-manage', {
        body: { action: 'bill_details', bill_id: billId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id && !!billId,
  });
}
